/**
 * iter297 — Onglet « Contrôles » de la comptabilité.
 *
 * L'ancien voyant « Bilan équilibré » était tautologique (débit = crédit dans chaque
 * écriture, donc toujours vert). Ici on cherche les anomalies réelles : devises,
 * doublons, comptes hors plan ou hors classe, dates inexploitables, et la
 * réconciliation Bilan ↔ Compte de résultat.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Info, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const STATUTS = {
  ok: { Icon: CheckCircle2, cls: "text-emerald-600", card: "border-emerald-200", badge: "bg-emerald-100 text-emerald-800" },
  attention: { Icon: AlertTriangle, cls: "text-amber-600", card: "border-amber-300", badge: "bg-amber-100 text-amber-800" },
  erreur: { Icon: XCircle, cls: "text-red-600", card: "border-red-300", badge: "bg-red-100 text-red-800" },
  info: { Icon: Info, cls: "text-blue-600", card: "border-blue-200", badge: "bg-blue-100 text-blue-800" },
};

const fmt = (n) => (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ControleItem = ({ c, onRefresh }) => {
  const [ouvert, setOuvert] = useState(false);
  const s = STATUTS[c.statut] || STATUTS.info;
  const colonnes = c.items?.length ? Object.keys(c.items[0]).filter((k) => k !== "id") : [];
  // iter301 — recodage TVA directement depuis le contrôle
  const recodable = c.code === "ventes_mal_codees";
  const [codes, setCodes] = useState([]);
  const [choix, setChoix] = useState({});

  useEffect(() => {
    if (!recodable || !ouvert || codes.length) return;
    api.get("/journal/codes-tva").then(({ data }) => setCodes(data || [])).catch(() => {});
  }, [recodable, ouvert, codes.length]);

  const recoder = async (item) => {
    const code = choix[item.id];
    if (!code) { toast.error("Choisis un code TVA"); return; }
    try {
      await api.patch(`/journal/ecritures/${item.id}`, { tva_code: code });
      toast.success(`Écriture recodée en ${code}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recodage impossible");
    }
  };

  return (
    <Card className={`p-3 ${s.card}`} data-testid={`controle-${c.code}`}>
      <div className="flex items-start gap-2">
        <s.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.cls}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{c.titre}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.badge}`}>{c.statut}</span>
          </div>
          <div className="text-xs text-gray-600 mt-0.5">{c.detail}</div>
          {c.action && <div className="text-xs text-gray-500 italic mt-1">→ {c.action}</div>}
          {c.items?.length > 0 && (
            <button
              type="button"
              className="mt-1.5 text-xs text-[#52a675] font-medium flex items-center gap-1"
              onClick={() => setOuvert((o) => !o)}
              data-testid={`controle-detail-${c.code}`}
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${ouvert ? "rotate-180" : ""}`} />
              {ouvert ? "Masquer" : `Voir les ${c.items.length} ligne(s)`}
            </button>
          )}
          {ouvert && c.items?.length > 0 && (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-gray-500 text-left">
                    {colonnes.map((k) => <th key={k} className="pr-3 pb-1 font-medium">{k.replace(/_/g, " ")}</th>)}
                    {recodable && <th className="pb-1 font-medium">nouveau code TVA</th>}
                  </tr>
                </thead>
                <tbody>
                  {c.items.map((it, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      {colonnes.map((k) => (
                        <td key={k} className="pr-3 py-1 text-gray-700 font-mono">
                          {typeof it[k] === "number" ? fmt(it[k]) : String(it[k] ?? "—").slice(0, 60)}
                        </td>
                      ))}
                      {recodable && (
                        <td className="py-1 whitespace-nowrap">
                          <select
                            className="border border-gray-200 rounded px-1 py-0.5 text-[11px] mr-1"
                            value={choix[it.id] || ""}
                            onChange={(e) => setChoix((p) => ({ ...p, [it.id]: e.target.value }))}
                            data-testid={`recode-select-${it.id}`}
                          >
                            <option value="">— choisir —</option>
                            {codes.filter((cd) => ["due", "exonere"].includes(cd.sens)).map((cd) => (
                              <option key={cd.code} value={cd.code}>{cd.libelle || cd.code}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="text-[11px] text-[#52a675] font-medium"
                            onClick={() => recoder(it)}
                            data-testid={`recode-btn-${it.id}`}
                          >
                            Appliquer
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const ControlesTab = () => {
  const today = new Date();
  const [from, setFrom] = useState(`${today.getFullYear()}-01-01`);
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/controles", { params: { date_from: from, date_to: to } });
      setData(data);
    } finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const s = STATUTS[data?.statut] || STATUTS.info;

  return (
    <div data-testid="controles-tab" className="space-y-3">
      <Card className="p-4 border-gray-200 flex items-center gap-2 flex-wrap">
        <Label className="text-xs uppercase text-gray-500">Exercice :</Label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="controles-from" />
        <span className="text-gray-400">→</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="controles-to" />
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="controles-relancer">
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
          Relancer
        </Button>
      </Card>

      {loading && !data && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

      {data && (
        <>
          <Card className={`p-4 ${s.card}`} data-testid="controles-resume">
            <div className="flex items-center gap-2">
              <s.Icon className={`w-5 h-5 ${s.cls}`} />
              <span className="text-base font-bold text-gray-900">
                {data.statut === "ok" ? "Aucune anomalie détectée"
                  : data.statut === "erreur" ? "Anomalie bloquante détectée"
                    : "Points à vérifier"}
              </span>
            </div>
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs text-gray-700">
              <div>Écritures au journal : <span className="font-mono">{data.resume.ecritures}</span></div>
              <div>Produits de l&apos;exercice : <span className="font-mono">{fmt(data.resume.produits_exercice)}</span></div>
              <div>Charges de l&apos;exercice : <span className="font-mono">{fmt(data.resume.charges_exercice)}</span></div>
              <div>Résultat de l&apos;exercice : <span className="font-mono font-semibold">{fmt(data.resume.resultat_exercice)}</span></div>
              <div>Résultat reporté : <span className="font-mono">{fmt(data.resume.resultat_reporte)}</span></div>
              <div>Actif / Passif : <span className="font-mono">{fmt(data.resume.total_actif)} / {fmt(data.resume.total_passif)}</span></div>
            </div>
          </Card>
          {data.controles.map((c) => <ControleItem key={c.code} c={c} onRefresh={fetchData} />)}
        </>
      )}
    </div>
  );
};

export default ControlesTab;
